import React, { Fragment, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import PerfectScrollbar from 'react-perfect-scrollbar'
import {
  File as FileIcon,
  Upload as UploadIcon,
  X as XIcon,
} from 'react-feather'
import { flatMap, isEmpty, isNil, size, uniq } from 'lodash'
import { ErrorCode, useDropzone } from 'react-dropzone'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core'

import { BaseDialog } from 'components'

import helpers from 'helpers'
import useSnackbar from 'hooks/useSnackbar'
import useWaitingText from 'hooks/useWaitingText'

import styles from './styles'

import theme from 'theme'
import constants from 'constants/index'

// Recomendações:
// Ao declarar o campo de anexos (attachments) com useForm,
// defina o valor inicial (defaultValues) como null caso não haja arquivos.
// Isso garante que o input de arquivos seja limpo ao resetar o formulário.
// Utilizar undefined pode causar problemas de comportamento.

const FileUploadInput = ({
  accept = constants.validations.ALL_FILE_TYPES,
  attachmentsBgcolor = theme.palette.custom.light,
  backgroundColor,
  blockUnaccepted = false,
  controllerBar = false,
  controlName,
  disabled = false,
  EndIcon = FileIcon,
  existingFileNames = [],
  hasDownloadFile = false,
  highlight = false,
  loading = false,
  maxFiles = 0,
  maxSize = constants.validations.MAX_FILE_SIZE,
  multiple = false,
  onRemoveApiFile = null, // Prop para remover arquivos da API(Precisa de ID do attachment vindo da API, se não a modal não abre)
  rejectText = 'Tipo de arquivo rejeitado',
  required = false,
  StartIcon = UploadIcon,
  subCaption = '',
  title = 'Arraste e solte ou selecione o documento a ser anexado',
  width,
}) => {
  const { isFileInList, defineIsPreview } = helpers.validates.file
  const { toFileList, handleOpen, toDropzoneAccept } = helpers.file

  const [draggedFilesCount, setDraggedFilesCount] = useState(0)
  const [isRemovingFile, setIsRemovingFile] = useState(false)
  const [confirmRemoveModal, setConfirmRemoveModal] = useState({
    open: false,
    file: null,
    index: null,
  })

  const snackbar = useSnackbar()
  const loadingText = useWaitingText({
    baseText: 'Carregando',
    active: loading,
  })

  const removeText = useWaitingText({
    baseText: 'Removendo',
    active: isRemovingFile,
  })

  const { control, watch, setValue, errors, register } = useFormContext()

  const selectedFile = watch(controlName)
  const selectedFileSize = selectedFile?.[0]?.size || selectedFile?.size

  const isArray =
    Array.isArray(selectedFile) || selectedFile instanceof FileList
  const isMultiple = isArray && size(selectedFile) > 1
  const isPositive = isArray && !isEmpty(selectedFile)

  const isPreview = defineIsPreview(selectedFile)

  const disabledText = 'O envio de anexos está desabilitado'

  // Objetos da API ({ id, url, name }) não são File e seriam descartados pelo
  // toFileList (DataTransfer); com eles a lista segue como array misto.
  const buildFileValue = (files) => {
    const hasApiObjects = files.some((file) => !(file instanceof File))

    return hasApiObjects ? files : toFileList(files)
  }

  const updateFileList = (acceptedFiles) => {
    const currentFiles = selectedFile ? Array.from(selectedFile) : []
    let updatedFiles

    if (multiple && controllerBar) {
      const newFiles = acceptedFiles.filter(
        (newFile) => !isFileInList(newFile, currentFiles),
      )

      updatedFiles = buildFileValue([...currentFiles, ...newFiles])
    } else if (multiple) {
      updatedFiles = toFileList(acceptedFiles)
    } else {
      updatedFiles = acceptedFiles[0] || null
    }

    return updatedFiles
  }

  const rejectionMessages = {
    [ErrorCode.FileInvalidType]: {
      message: `Somente arquivos ${helpers.formatters.file.allowedTypes(
        accept,
      )} são permitidos`,
      variant: 'warning',
    },
    [ErrorCode.FileTooLarge]: {
      message: `O arquivo excede o limite de ${helpers.formatters.file.size(
        maxSize,
      )}`,
      variant: 'error',
    },
    [ErrorCode.TooManyFiles]: {
      message: `Selecione no máximo ${maxFiles} arquivo(s)`,
      variant: 'error',
    },
  }

  const notifyRejections = (fileRejections) => {
    const errorCodes = uniq(
      flatMap(fileRejections, ({ errors: fileErrors }) =>
        fileErrors.map((fileError) => fileError.code),
      ),
    )

    errorCodes.forEach((errorCode) => {
      if (rejectionMessages[errorCode])
        snackbar.open(rejectionMessages[errorCode])
    })
  }

  // Filtra e exibe alerta se houver arquivos com o mesmo nome de arquivos já existentes
  const filterAndWarnDuplicates = (filesArray) => {
    if (isEmpty(existingFileNames)) return filesArray

    const nonDuplicates = filesArray.filter(
      (file) => !existingFileNames.includes(file.name),
    )

    if (size(nonDuplicates) < size(filesArray)) {
      snackbar.open({
        message:
          'Alguns arquivos não foram adicionados por já existirem na lista.',
        variant: 'warning',
      })
    }

    return nonDuplicates
  }

  const handleDragEnter = (event) =>
    setDraggedFilesCount(size(event?.dataTransfer?.items))

  const handleDrop = (acceptedFiles, fileRejections) => {
    notifyRejections(fileRejections)

    const newFiles = filterAndWarnDuplicates(acceptedFiles)

    if (isEmpty(newFiles)) {
      if (!isEmpty(fileRejections) || !isEmpty(acceptedFiles) || controllerBar)
        return

      return setValue(controlName, null, true)
    }

    setValue(controlName, updateFileList(newFiles), true)
  }

  const { getRootProps, getInputProps, inputRef, isDragActive, isDragReject } =
    useDropzone({
      onDrop: handleDrop,
      accept: blockUnaccepted ? toDropzoneAccept(accept) : undefined,
      ...(maxSize && { maxSize }),
      maxFiles,
      multiple: true,
      disabled: disabled || loading,
      noDragEventsBubbling: true,
      useFsAccessApi: false,
    })

  const hasTooManyFiles = maxFiles >= 1 && draggedFilesCount > maxFiles

  const dragRejectText = hasTooManyFiles
    ? rejectionMessages[ErrorCode.TooManyFiles].message
    : rejectText

  const colorInput = () => {
    if (disabled || loading) return theme.palette.secondary.light
    else if (isDragReject) return theme.palette.error.main
    else if (errors?.[controlName])
      return isDragActive ? theme.palette.error.dark : theme.palette.error.main
    return isDragActive
      ? theme.palette.primary.hover
      : theme.palette.custom.dark
  }

  const useStyles = styles({
    attachmentsBgcolor,
    backgroundColor,
    color: colorInput(),
    isDragActive,
    highlight,
    width,
    disabled: disabled || loading,
    error: !!errors?.[controlName],
    hasDownloadFile,
  })
  const classes = useStyles()

  const handleRemoveFile = (event, index) => {
    event.stopPropagation()
    if (disabled || loading) return
    if (isNil(index)) {
      setValue(controlName, null)
      if (inputRef.current) inputRef.current.value = ''
      return
    }

    const fileToRemove = selectedFile[index] || selectedFile

    // Se é um arquivo da API (tem ID) e a função de remoção foi passada
    if (onRemoveApiFile && fileToRemove?.id) {
      setConfirmRemoveModal({
        open: true,
        file: fileToRemove,
        index: index,
      })
      return
    }

    const currentFiles = Array.from(selectedFile) || []
    currentFiles.splice(index, 1)

    const updatedFiles = buildFileValue(currentFiles)
    setValue(controlName, size(updatedFiles) > 0 ? updatedFiles : null)

    if (inputRef.current) inputRef.current.value = ''
  }

  const handleCancelRemoveApiFile = () => {
    setConfirmRemoveModal({ open: false, file: null, index: null })
  }

  const handleConfirmRemoveApiFile = async () => {
    if (!onRemoveApiFile) return

    const { file, index } = confirmRemoveModal
    setIsRemovingFile(true)

    try {
      // Chama a função para remover o arquivo da API
      await onRemoveApiFile(file.id)

      // Remove o arquivo da lista local também
      const currentFiles = Array.from(selectedFile) || []
      currentFiles.splice(Number(index), 1)

      const files = buildFileValue(currentFiles)
      setValue(controlName, size(files) > 0 ? files : null)

      if (inputRef.current) inputRef.current.value = ''

      snackbar.open({
        message: 'Arquivo removido com sucesso!',
        variant: 'success',
      })
    } catch {
      snackbar.open({
        message: 'Erro ao remover arquivo. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsRemovingFile(false)
      handleCancelRemoveApiFile()
    }
  }

  useEffect(() => {
    if (required) {
      register(controlName, { required: 'Este campo é obrigatório' })
    }
  }, [register, controlName, required])

  return (
    <Box>
      <Controller
        as={
          <FormControl
            {...getRootProps({ onDragEnter: handleDragEnter })}
            className={classes.formControl}
            disabled={disabled || loading}
          >
            <input
              {...getInputProps({ accept: accept || '*/*', multiple })}
              aria-label="Selecionar arquivo"
              className={classes.input}
              disabled={disabled || loading}
            />
            {loading ? (
              <Fragment>
                <CircularProgress size={24} color="inherit" />
                <Typography variant="caption">{loadingText}</Typography>
              </Fragment>
            ) : isNil(selectedFile) || selectedFile?.length === 0 ? (
              <Fragment>
                {isDragReject ? <XIcon /> : <StartIcon />}
                <Typography component="span" className={classes.title}>
                  {isDragReject
                    ? dragRejectText
                    : disabled
                    ? disabledText
                    : title}
                </Typography>
                {accept && !disabled && (
                  <Box display="flex" flexDirection="column">
                    <Typography variant="caption">
                      Arquivos suportados:&nbsp;
                      {helpers.formatters.file.allowedTypes(accept)}
                    </Typography>
                    {maxSize && (
                      <Typography variant="caption">
                        Tamanho máximo: {helpers.formatters.file.size(maxSize)}
                      </Typography>
                    )}
                    {subCaption && (
                      <Typography variant="caption">{subCaption}</Typography>
                    )}
                  </Box>
                )}
              </Fragment>
            ) : (
              <Fragment>
                <Box
                  display="flex"
                  alignItems="center"
                  maxWidth="90%"
                  gridGap={theme.spacing(2)}
                >
                  <Box>
                    <EndIcon size={20} />
                  </Box>
                  <Typography noWrap>
                    {!isMultiple
                      ? selectedFile[0]?.name || selectedFile?.name
                      : `${size(selectedFile)} arquivos selecionados`}
                  </Typography>
                  {!isMultiple && selectedFileSize && (
                    <Box>
                      <Typography variant="caption" noWrap width="fit-content">
                        {helpers.formatters.file.size(selectedFileSize)}
                      </Typography>
                    </Box>
                  )}
                  {!controllerBar && (
                    <Tooltip title="Remover arquivos" arrow>
                      <IconButton
                        size="small"
                        onClick={(event) => handleRemoveFile(event)}
                        disabled={disabled}
                      >
                        <XIcon size={14} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="caption">
                  {disabled
                    ? disabledText
                    : `Clique ou arraste para ${
                        isMultiple || (controllerBar && isPositive)
                          ? 'adicionar um novo'
                          : 'substituir o'
                      } arquivo`}
                </Typography>
                {!isMultiple && hasDownloadFile && !controllerBar && (
                  <Tooltip
                    title={`Clique aqui para ${
                      isPreview ? 'visualizar' : 'baixar'
                    } o arquivo. Caso não ocorra, verifique se não possui um bloqueador de pop-ups`}
                    arrow
                  >
                    <Box>
                      <Button
                        variant="outlined"
                        color="secondary"
                        className={classes.previewButton}
                        onClick={(event) => {
                          event.stopPropagation()
                          handleOpen(selectedFile)
                        }}
                      >
                        {`${isPreview ? 'Visualizar' : 'Baixar'} o arquivo`}
                      </Button>
                    </Box>
                  </Tooltip>
                )}
              </Fragment>
            )}
          </FormControl>
        }
        control={control}
        name={controlName}
        onChange={() => selectedFile}
        mode="onChange"
      />
      {!!errors?.[controlName] && (
        <FormHelperText error={!!errors?.[controlName]}>
          {<>{String(errors?.[controlName]?.message)}</>}
        </FormHelperText>
      )}
      {controllerBar && isPositive && (
        <Box className={classes.controlBar}>
          <PerfectScrollbar
            options={{ wheelPropagation: true, useBothWheelAxes: true }}
          >
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gridGap={theme.spacing(2)}
              pb={2}
            >
              {Array.from(selectedFile)?.map((file, index) => {
                const fileItemIsPreview =
                  file instanceof File &&
                  constants.validations.OPENABLE_FILE_MIME_TYPES.includes(
                    file.type,
                  )

                return (
                  <Box
                    key={index}
                    className={classes.attachmentItem}
                    {...(hasDownloadFile && {
                      onClick: () => handleOpen(file),
                    })}
                  >
                    <Tooltip
                      title={
                        hasDownloadFile
                          ? `Clique aqui para ${
                              fileItemIsPreview ? 'visualizar' : 'baixar'
                            } o arquivo. Caso não ocorra, verifique se não possui um bloqueador de pop-ups`
                          : ''
                      }
                      placement="top"
                      arrow
                    >
                      <Box
                        display="flex"
                        gridGap={theme.spacing(1)}
                        className={classes.attachmentInfo}
                      >
                        <EndIcon size={18} className={classes.attachmentIcon} />
                        <Typography className={classes.attachmentName}>
                          {file?.name}
                        </Typography>
                      </Box>
                    </Tooltip>
                    {!disabled && (
                      <IconButton
                        size="small"
                        onClick={(event) => handleRemoveFile(event, index)}
                      >
                        <XIcon size={14} />
                      </IconButton>
                    )}
                  </Box>
                )
              })}
            </Box>
          </PerfectScrollbar>
        </Box>
      )}
      <BaseDialog
        open={confirmRemoveModal.open}
        setOpen={handleCancelRemoveApiFile}
        preventCloseOnBackdropClick={isRemovingFile}
        title="Confirmar remoção do arquivo"
        dialogSize="sm"
        dialogActions={
          <Box display="flex" gridGap={theme.spacing(1)}>
            <Button
              type="button"
              variant="outlined"
              onClick={handleCancelRemoveApiFile}
              disabled={isRemovingFile}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={handleConfirmRemoveApiFile}
              disabled={isRemovingFile}
            >
              {isRemovingFile ? removeText : 'Remover'}
            </Button>
          </Box>
        }
      >
        <Box py={2}>
          <Typography>
            Tem certeza que deseja remover o arquivo "
            <strong>{confirmRemoveModal.file?.name}</strong>"? Esta ação não
            pode ser desfeita.
          </Typography>
        </Box>
      </BaseDialog>
    </Box>
  )
}

FileUploadInput.propTypes = {
  accept: PropTypes.string,
  attachmentsBgcolor: PropTypes.string,
  backgroundColor: PropTypes.string,
  controllerBar: PropTypes.bool,
  controlName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  EndIcon: PropTypes.elementType,
  existingFileNames: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  hasDownloadFile: PropTypes.bool,
  blockUnaccepted: PropTypes.bool,
  highlight: PropTypes.bool,
  maxFiles: PropTypes.number,
  maxSize: PropTypes.number,
  rejectText: PropTypes.string,
  multiple: PropTypes.bool,
  onRemoveApiFile: PropTypes.func,
  required: PropTypes.bool,
  StartIcon: PropTypes.elementType,
  subCaption: PropTypes.string,
  title: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

export default FileUploadInput
